/**
 * ICU parse-error guard.
 *
 * When a translation value carries ICU placeholders (`{name}`, `{count, plural,
 * …}`) but the caller passes no matching arguments, intl-messageformat throws
 * while formatting. i18next-icu's default `parseErrorHandler` then returns the
 * RAW template verbatim, so the user sees literal `{count, plural, …}` text on
 * screen (this is how the insufficient-leave-balance message shipped as a raw
 * ICU template to end users).
 *
 * This handler is the last line of defence for that failure mode. It is GLOBAL
 * (every namespace), so it must never make a message WORSE than the library
 * default. Two cases:
 *
 *   - Caller passed some interpolation args but formatting still threw (a subset
 *     missing / a bad arg): re-run a LENIENT format so the args the caller DID
 *     pass still render — a blanket strip would nuke `{name}` too, and returning
 *     the raw template would show braces.
 *   - Caller passed nothing (the genuine no-args case, e.g. the balance submit):
 *     strip the placeholder blocks for clean prose ("… available, requested.").
 *
 * It never returns an empty string: an ICU-only message that strips to nothing
 * is recovered via a lenient format, and the last resort is the key's final
 * segment. It warns once per key so the missing-argument call site gets found.
 */

/**
 * Remove balanced `{ … }` placeholder groups, including nested braces (ICU
 * plural/select blocks nest), then tidy the whitespace and dangling
 * punctuation the removed blocks leave behind.
 */
export function stripIcuPlaceholders(input: string): string {
  let out = "";
  let depth = 0;
  for (const ch of input) {
    if (ch === "{") {
      depth += 1;
    } else if (ch === "}") {
      if (depth > 0) depth -= 1;
    } else if (depth === 0) {
      out += ch;
    }
  }
  return out
    .replace(/\s{2,}/g, " ") // collapse runs of whitespace left by removed blocks
    .replace(/\s+([.,;:!?،؛])/g, "$1") // pull punctuation back onto the prior word
    .trim();
}

export type IcuParseErrorHandler = (
  err: Error,
  key: string,
  res: string,
  options: object,
) => string;

/** Minimal structural type for the intl-messageformat constructor. */
export type IntlMessageFormatCtor = new (
  message: string,
  locales?: string | string[],
  formats?: unknown,
  opts?: { ignoreTag?: boolean },
) => { format: (values: Record<string, unknown>) => unknown };

export interface IcuParseErrorHandlerOptions {
  /** Warning sink (defaults to `console.warn`) — injectable for tests. */
  warn?: (message: string, err: unknown) => void;
  /**
   * Returns the intl-messageformat constructor for the lenient re-format.
   * i18next-icu assigns it to `i18next.IntlMessageFormat` at init; wire that
   * lazily here. When absent (unit tests without init), the lenient path is
   * skipped and the handler falls back to stripping.
   */
  getIntlMessageFormat?: () => IntlMessageFormatCtor | undefined;
  /** Current resolved locale for the lenient re-format (defaults to "en"). */
  getLocale?: () => string | undefined;
}

/**
 * i18next `t()` option keys that are control flags, NOT interpolation values.
 * These must be excluded when deciding whether the caller passed real args,
 * because `useTranslation()` / `getFixedT()` ALWAYS inject some of them onto
 * every call's options (notably an enumerable `keyPrefix`, even when
 * undefined) — counting those as user vars would route genuine no-argument
 * calls through the lenient path and render "0" instead of the stripped text.
 *
 * A key is reserved ONLY when it is both (1) auto-injected by i18next or a pure
 * control flag, AND (2) never a realistic ICU `{var}` name a caller would
 * interpolate. Keys that a caller legitimately passes as message variables are
 * deliberately NOT reserved, so they flip to the lenient (preserve-args) path:
 *   - `count`   — the plural variable.
 *   - `context` — a select variable (`{context}`); the caller supplies it, and
 *     i18next passes it through to ICU. Reserving it dropped "admin" from
 *     `t("role", { context: "admin" })`.
 *   - `ordinal` — a plausible number variable in a message.
 *   - `replace` — a caller-supplied value container, not a control-only flag.
 */
const RESERVED_OPTION_KEYS = new Set<string>([
  "keyPrefix",
  "lng",
  "lngs",
  "ns",
  "defaultValue",
  "defaultVariables",
  "interpolation",
  "keySeparator",
  "nsSeparator",
  "fallbackLng",
  "returnObjects",
  "returnDetails",
  "returnEmptyString",
  "returnNull",
  "joinArrays",
  "postProcess",
]);

/** Did the caller pass any actual interpolation variables (vs only control flags)? */
function callerSuppliedVars(options: unknown): boolean {
  if (options === null || typeof options !== "object") return false;
  return Object.keys(options as Record<string, unknown>).some((k) => !RESERVED_OPTION_KEYS.has(k));
}

function isRenderable(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

/** The key's final segment (`ns:a.b.c` → `c`) — a non-blank last-resort label. */
function lastKeySegment(key: string): string {
  const afterNs = key.includes(":") ? key.slice(key.indexOf(":") + 1) : key;
  const parts = afterNs.split(".");
  return parts[parts.length - 1] ?? "";
}

/**
 * Re-run the ICU format leniently: the provided args render as-is; any missing
 * (or nullish) variable resolves to `0`, which is a valid plural/select
 * selector AND renders as "0" in a plain placeholder — so the message never
 * throws again and never blanks. Returns `null` if intl-messageformat is
 * unavailable or the re-format still fails.
 */
function lenientReformat(
  res: string,
  options: unknown,
  getIntlMessageFormat: (() => IntlMessageFormatCtor | undefined) | undefined,
  getLocale: (() => string | undefined) | undefined,
): string | null {
  try {
    const Ctor = getIntlMessageFormat?.();
    if (typeof Ctor !== "function") return null;
    const locale = getLocale?.() ?? "en";
    const base =
      options !== null && typeof options === "object" ? (options as Record<string, unknown>) : {};
    const values = new Proxy(base, {
      // `has` must report every variable as present, otherwise
      // intl-messageformat throws "variable X was not provided" before it ever
      // reads the value through `get`. `for…in` still enumerates only the real
      // keys (no ownKeys trap), so this doesn't leak phantom keys.
      has() {
        return true;
      },
      get(target, prop: string | symbol) {
        const supplied = typeof prop === "string" && prop in target ? target[prop] : undefined;
        return supplied === undefined || supplied === null ? 0 : supplied;
      },
    });
    const out = new Ctor(res, locale, undefined, { ignoreTag: true }).format(values);
    return typeof out === "string" ? out : null;
  } catch {
    return null;
  }
}

/**
 * Build the i18next-icu `parseErrorHandler`. Each distinct key is warned about
 * only once per process so a hot render loop doesn't flood the console.
 */
export function createIcuParseErrorHandler(
  options: IcuParseErrorHandlerOptions = {},
): IcuParseErrorHandler {
  const warn = options.warn ?? ((message, err) => console.warn(message, err));
  const { getIntlMessageFormat, getLocale } = options;
  const warnedKeys = new Set<string>();

  return (err, key, res, opts) => {
    if (!warnedKeys.has(key)) {
      warnedKeys.add(key);
      warn(
        `[i18n] ICU formatting failed for "${key}" — placeholders left unfilled; ` +
          `pass the required arguments at the call site.`,
        err,
      );
    }

    if (callerSuppliedVars(opts)) {
      // Partial args: preserve the variables the caller did supply.
      const lenient = lenientReformat(res, opts, getIntlMessageFormat, getLocale);
      if (isRenderable(lenient)) return lenient;
      // No lenient formatter available — return the template rather than
      // stripping the parts the caller expected to see.
      return isRenderable(res) ? res : lastKeySegment(key) || res;
    }

    // Genuine no-args case: strip placeholder blocks for clean prose.
    const stripped = stripIcuPlaceholders(res);
    if (isRenderable(stripped)) return stripped;
    // An ICU-only message strips to empty — recover a non-blank string via a
    // lenient format (safe defaults) so a plural/select value never renders "".
    const lenient = lenientReformat(res, opts, getIntlMessageFormat, getLocale);
    if (isRenderable(lenient)) return lenient;
    return lastKeySegment(key) || res;
  };
}
